import { useCallback } from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

export function AlertDialogRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const isOpen = element.variant === "open"
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined

  const handleConfirm = useCallback(() => {
    dispatchEvent(element.id, "confirm", {})
  }, [element.id, dispatchEvent])

  const handleCancel = useCallback(() => {
    dispatchEvent(element.id, "cancel", {})
  }, [element.id, dispatchEvent])

  return (
    <AlertDialogPrimitive.Root open={isOpen}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <AlertDialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg",
            element.className,
          )}
          data-testid={`alert-dialog-${element.id}`}
        >
          {text && (
            <AlertDialogPrimitive.Title className="text-lg font-semibold">
              {text}
            </AlertDialogPrimitive.Title>
          )}
          <div className="flex justify-end gap-2">
            <AlertDialogPrimitive.Cancel
              className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              onClick={handleCancel}
            >
              Cancel
            </AlertDialogPrimitive.Cancel>
            <AlertDialogPrimitive.Action
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              onClick={handleConfirm}
            >
              Confirm
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  )
}
